import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import { IItemCreationHeader, ItemCreationSection } from '~/Components/ItemCreation';
import { useBoolean, useDateFns } from '~/Hooks';
import { ExtendedEventSeries } from '../../extendedEventSeries.model';
import { EventScheduleTimeSeriesEditor } from '../EventScheduleTimeSeriesEditor/EventScheduleTimeSeriesEditor';
import { EventType } from '~/Models/DTO/Event.model';
import { TIME_FREQUENCY } from '~/Models/DTO/EventSeries.model';
import { WeekDays, WeekDaysMap } from '~/Models/interfaces/WeekDays.model';

export const useEventScheduleTimeSeriesItem = ({
  timeSeries,
  onUpdate,
  onUpdateSingleValue,
  onDelete,
  eventType,
}: {
  timeSeries: ExtendedEventSeries;
  onUpdate: (timeSeries: ExtendedEventSeries) => void;
  onUpdateSingleValue: (name: string, value: any) => void;
  onDelete: () => void;
  eventType: EventType;
}) => {
  const { t } = useTranslation();
  const { formatDate, localeDateFormat, currentLocale } = useDateFns();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { state: isEditModalOpen, toggle: toggleEditModalOpen } = useBoolean(
    timeSeries.openByDefault,
  );

  const initialValues = useMemo(() => timeSeries, [isEditModalOpen]);

  const headers: IItemCreationHeader[] = [
    {
      index: 0,
      testId: 'timeSeries.general',
      title: t('app.title.general'),
      icon: <div />,
    },
  ];
  const listItems: ItemCreationSection<ExtendedEventSeries>[] = useMemo(
    () => [({ data }) => EventScheduleTimeSeriesEditor({ timeSeries: data, onDelete, eventType })],
    [eventType],
  );

  const onCancel = () => {
    if (isMobile) {
      if (!timeSeries.savedOnMobile) {
        onDelete();
      } else {
        onUpdate(initialValues);
      }
    }
    toggleEditModalOpen();
  };

  const onSave = () => {
    onUpdateSingleValue('savedOnMobile', true);
    toggleEditModalOpen();
  };

  const timeRange =
    ![EventType.FREE_TRAINING].includes(eventType) &&
    `${formatDate(timeSeries.dailyStartTime, 'HH:mm')}-${formatDate(
      timeSeries.dailyEndTime,
      'HH:mm',
    )}`;

  const timeRecurrenceName = useMemo(() => {
    switch (timeSeries.recurrence) {
      case TIME_FREQUENCY.MONTHLY: {
        return `${t('time.frequency.Monthly')} `;
      }
      case TIME_FREQUENCY.WEEKLY: {
        return `${t('time.frequency.Weekly')} `;
      }
      default:
        return ' ';
    }
  }, [timeSeries.recurrence]);

  const endDate = timeSeries.hasEndDate
    ? `${t('time.ends')} ${formatDate(timeSeries.endDate, localeDateFormat)}`
    : t('time.noEnd');
  const selectedWeekDays = useMemo(() => WeekDays.filter((weekday) => timeSeries[weekday]), [
    timeSeries,
  ]);
  const weekDayLabels = useMemo(
    () =>
      selectedWeekDays
        .map((weekDay) =>
          currentLocale.localize?.day(WeekDaysMap[weekDay], {
            width: 'abbreviated',
          }),
        )
        .join(', '),
    [selectedWeekDays, currentLocale],
  );
  const subtitle = useMemo(() => [weekDayLabels, endDate].filter(Boolean).join(' | '), [
    weekDayLabels,
    endDate,
  ]);

  useEffect(() => {
    onUpdateSingleValue('openByDefault', false);
  }, []);

  return {
    timeRange,
    subtitle,
    timeRecurrenceName,
    listItems,
    headers,
    isEditModalOpen: isEditModalOpen && !timeSeries.shouldDelete,
    toggleEditModalOpen,
    onCancel,
    onSave,
    isMobile,
  };
};
